// Codebase analysis of one PRD version (existing-project onboarding, spec §2.1, §2.3, §5; decision D26; owner answers
// 7a-7c): start ("analyse the code", maintainer+) with the plain-language refusal cause, follow an active run (polled
// every 3 s) and cancel it, pick any listed run (newest first; its cost, closures and bulk undo are per run), answer the review list (would-close first, "ready" / "not ready"), the dry run of a run
// without auto-close, and the closures with their single and bulk undo. Evidence is paths and line ranges only:
// no code text is ever shown.
import { useEffect, useRef, useState } from "react";
import type { ProjectSummary } from "@maestros/contracts";
import { useI18n } from "../../i18n/index.tsx";
import type { Translate } from "../../i18n/index.tsx";
import { isApiError } from "../../api/client.ts";
import { taskAction } from "../../api/endpoints.ts";
import { answerCodebaseAnalysis, cancelCodebaseAnalysis, getCodebaseAnalysis, listCodebaseAnalyses, reopenCodebaseAnalysis, startCodebaseAnalysis } from "../../api/endpoints-onboarding.ts";
import type { AnalysisAnswerRequest, ClosedItem, CodebaseAnalysisRun, CodeVerdict, CodeVerdictRow, EvidenceRef, ReviewItem } from "../../api/types-onboarding.ts";
import { useAsync } from "../../lib/useAsync.ts";
import { statusView, type StatusView } from "../../lib/status.ts";
import { Button } from "../../components/Button.tsx";
import { Banner, ErrorBanner, LoadingBlock, localErrorMessage } from "../../components/Feedback.tsx";
import { SelectField } from "../../components/Field.tsx";
import { Pill, StatusPill } from "../../components/Status.tsx";
import { useToast } from "../../components/Toast.tsx";
import { ConfirmDialog, useProjectRole } from "../wave3/common.tsx";
import { formatUsd } from "../routing/shared.tsx";
import { BoardStartedDependents } from "./StartedDependents.tsx";

const POLL_MS = 3000;
const SHOWN_REFS = 3;
const CAUSES = ["disabled", "not_attested", "no_repository", "no_jev_connection", "unavailable", "analysis_running", "no_machine_verdict", "no_evidence", "project_halted"] as const;
/** Why a task was left as it was by "ready" (closeRequirementAsExisting) or by the bulk undo (reopenAnalysisClosures). */
const SKIP_CAUSES = ["invalid_transition", "children_pending", "gate_pending", "dependencies_pending", "shared_requirement_open", "touched", "not_existing"] as const;

type Skipped = { task_id: string; reason: string }[];
type Undo = { kind: "one"; item: ClosedItem } | { kind: "all"; ids: string[] };

const VERDICT_VIEW: Record<CodeVerdict, StatusView> = {
  implemented: { tone: "success", icon: "check", label: "onboarding.verdict.implemented" },
  partial: { tone: "info", icon: "activity", label: "onboarding.verdict.partial" },
  not_found: { tone: "neutral", icon: "circle", label: "onboarding.verdict.not_found" },
  uncertain: { tone: "warning", icon: "alert", label: "onboarding.verdict.uncertain" },
};

const RUN_VIEW: Record<CodebaseAnalysisRun["state"], StatusView> = {
  queued: { tone: "neutral", icon: "clock", label: "onboarding.state.queued" },
  running: { tone: "info", icon: "spinner", label: "onboarding.state.running" },
  done: { tone: "success", icon: "check", label: "onboarding.state.done" },
  failed: { tone: "danger", icon: "x", label: "onboarding.state.failed" },
  cancelled: { tone: "neutral", icon: "ban", label: "onboarding.state.cancelled" },
};

const isActive = (run: CodebaseAnalysisRun | undefined) => run?.state === "queued" || run?.state === "running";

/** The refusal in plain language: the known `details.cause` (or the unavailable engine), else the server's message. */
function refusalText(t: Translate, e: unknown): string {
  if (isApiError(e)) {
    const raw = e.details?.["cause"] ?? (e.status === 503 ? "unavailable" : null);
    const cause = CAUSES.find((c) => c === raw);
    if (cause) return t(`onboarding.cause.${cause}`);
  }
  return localErrorMessage(e, t);
}

/** Up to three `path:start–end` references, then `+N`; paths and line ranges only. */
export function EvidenceRefs({ refs }: { refs: readonly EvidenceRef[] }) {
  const { t } = useI18n();
  if (refs.length === 0) return null;
  const rest = refs.length - SHOWN_REFS;
  return (
    <ul className="plain-list evidence-refs" aria-label={t("onboarding.refsLabel")}>
      {refs.slice(0, SHOWN_REFS).map((r) => (
        <li key={`${r.path}:${r.start}`} className="mono user-content">
          {`${r.path}:${r.start}–${r.end}`}
        </li>
      ))}
      {rest > 0 ? <li className="subtle">{t("onboarding.moreRefs", { count: rest })}</li> : null}
    </ul>
  );
}

function NotHomologated() {
  const { t } = useI18n();
  return (
    <Pill tone="warning" icon="alert">
      {t("onboarding.notHomologated")}
    </Pill>
  );
}

/** The "Code" cell of the coverage table: verdict, "not validated" when only fixtures back it (7a), the files. */
export function CodeVerdictCell({ verdict }: { verdict: CodeVerdictRow | null | undefined }) {
  const { t } = useI18n();
  if (!verdict) return <span className="subtle">{t("onboarding.notAnalysed")}</span>;
  return (
    <div className="stack code-verdict" data-testid="code-verdict">
      <div className="row row--wrap">
        <StatusPill view={VERDICT_VIEW[verdict.verdict]} />
        {verdict.fixture_only ? <NotHomologated /> : null}
      </div>
      <EvidenceRefs refs={verdict.evidence.refs} />
    </div>
  );
}

export function CodebaseAnalysisPanel({ project, prdVersionId, onChanged }: { project: ProjectSummary; prdVersionId: string; onChanged: () => void }) {
  const { t, locale, formatDateTime } = useI18n();
  const { toast } = useToast();
  const canAct = useProjectRole(project.id).atLeast("maintainer");
  const runs = useAsync((signal) => listCodebaseAnalyses(project.id, prdVersionId, signal), [project.id, prdVersionId]);
  const runItems = runs.data?.items ?? [];
  const [chosenId, setChosenId] = useState<string | null>(null);
  const runId = chosenId ?? runItems[0]?.id ?? null;
  const detail = useAsync((signal) => (runId ? getCodebaseAnalysis(runId, signal) : Promise.resolve(null)), [runId]);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [undo, setUndo] = useState<Undo | null>(null);
  const [skipped, setSkipped] = useState<{ id: string; label: string; reason: string }[]>([]);

  const run = detail.data?.run;
  const active = isActive(run);
  const reload = detail.reload;

  /** The tasks an action left as they were, each with its reason in plain language (never a success-only toast). */
  const noteSkipped = (list: Skipped, known: readonly { id: string; number: number; title: string }[]) =>
    setSkipped(
      list.map((s) => {
        const task = known.find((k) => k.id === s.task_id);
        const cause = SKIP_CAUSES.find((c) => c === s.reason);
        return { id: s.task_id, label: task ? `#${task.number} ${task.title}` : s.task_id.slice(0, 8), reason: cause ? t(`onboarding.skip.${cause}`) : s.reason };
      }),
    );

  useEffect(() => {
    if (!active) return;
    const handle = window.setInterval(reload, POLL_MS);
    return () => window.clearInterval(handle);
  }, [active, reload]);

  // A run that just finished may have closed tasks: the coverage and the project are reloaded once.
  const wasActive = useRef(false);
  useEffect(() => {
    if (wasActive.current && !active && run) {
      onChanged();
      runs.reload();
    }
    wasActive.current = active;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, run?.id]);

  const act = async (key: string, work: () => Promise<string>) => {
    setBusy(key);
    setActionError(null);
    setSkipped([]);
    try {
      toast(await work(), "success");
      reload();
      onChanged();
    } catch (e) {
      setActionError(refusalText(t, e));
      reload();
    } finally {
      setBusy(null);
    }
  };

  const start = async () => {
    setBusy("start");
    setRefusal(null);
    try {
      const result = await startCodebaseAnalysis(project.id, prdVersionId);
      setChosenId(result.run.id);
      runs.reload();
      reload();
    } catch (e) {
      setRefusal(refusalText(t, e));
    } finally {
      setBusy(null);
    }
  };

  const cancel = async () => {
    if (!run) return;
    setBusy("cancel");
    setActionError(null);
    try {
      const next = await cancelCodebaseAnalysis(run.id);
      detail.setData((d) => (d ? { ...d, run: next } : d));
    } catch (e) {
      setActionError(refusalText(t, e));
    } finally {
      setBusy(null);
    }
  };

  const answer = (item: ReviewItem, value: AnalysisAnswerRequest["answer"]) =>
    act(item.requirement.id, async () => {
      const r = await answerCodebaseAnalysis(run!.id, { requirement_id: item.requirement.id, answer: value });
      noteSkipped(r.skipped, item.open_tasks);
      const code = item.requirement.external_id;
      return value === "ready" ? t("onboarding.answeredReady", { code, count: r.closed.length }) : t("onboarding.answeredNotReady", { code });
    });

  // Single and bulk undo both confirm first, listing the started dependents of what they reopen (7b, C4). The run is
  // reloaded even after an error, and a retry reads the task's version from that reload (never a stale one).
  const confirmUndo = async (u: Undo) => {
    setActionError(null);
    setSkipped([]);
    try {
      if (u.kind === "one") {
        const task = closed.find((c) => c.task.id === u.item.task.id)?.task ?? u.item.task;
        await taskAction(task.id, { action: "reopen", expected_version: task.version });
        toast(t("onboarding.taskReopened", { number: task.number }), "success");
      } else {
        const r = await reopenCodebaseAnalysis(run!.id);
        noteSkipped(r.skipped, closed.map((c) => c.task));
        toast(t("onboarding.reopenedCount", { count: r.reopened.length }), "success");
      }
      onChanged();
    } finally {
      reload();
    }
  };

  const review = [...(detail.data?.review ?? [])].sort((a, b) => Number(b.reason === "would_close") - Number(a.reason === "would_close"));
  const closed = detail.data?.closed ?? [];
  const wouldClose = new Set(review.filter((r) => r.reason === "would_close").flatMap((r) => r.open_tasks.map((x) => x.id))).size;
  const dryRun = run?.auto_close === false;
  const reopenable = closed.some((c) => c.reopenable);
  const answerable = canAct && !active;

  return (
    <section className="analysis-panel stack" aria-labelledby={`analysis-${prdVersionId}`}>
      <div className="row row--wrap row--between">
        <div className="stack" style={{ gap: 2, minWidth: 0 }}>
          <h3 id={`analysis-${prdVersionId}`} className="subheading">
            {t("onboarding.title")}
          </h3>
          <p className="field-hint">{t("onboarding.intro")}</p>
        </div>
        {canAct ? (
          <div className="row row--wrap">
            {active ? (
              <Button size="sm" loading={busy === "cancel"} onClick={() => void cancel()}>
                {t("onboarding.cancel")}
              </Button>
            ) : (
              <Button size="sm" variant="primary" loading={busy === "start"} onClick={() => void start()}>
                {t("onboarding.analyze")}
              </Button>
            )}
          </div>
        ) : null}
      </div>
      {runItems.length > 1 ? (
        <SelectField label={t("onboarding.runSelect")} value={runId ?? ""} onChange={(e) => setChosenId(e.target.value)}>
          {runItems.map((r) => (
            <option key={r.id} value={r.id}>
              {`${formatDateTime(r.created_at)} — ${t(RUN_VIEW[r.state].label)}`}
            </option>
          ))}
        </SelectField>
      ) : null}
      {refusal ? (
        <p className="tone-warning" role="alert" data-testid="analysis-refusal">
          {refusal}
        </p>
      ) : null}
      {runs.error ? <ErrorBanner error={runs.error} onRetry={runs.reload} /> : null}
      {detail.error ? <ErrorBanner error={detail.error} onRetry={reload} /> : null}
      {runs.loading || (runId && detail.loading) ? <LoadingBlock label={t("common.loading")} lines={2} /> : null}
      {run ? (
        <>
          <div className="row row--wrap">
            <StatusPill view={RUN_VIEW[run.state]} />
            <span className="muted">{t("onboarding.counts", run.counts)}</span>
            <span className="muted" data-testid="analysis-cost">
              {t("onboarding.cost", { cost: formatUsd(run.cost_usd, locale, t) })}
            </span>
          </div>
          {run.error ? <p className="tone-danger user-content">{t("onboarding.runError", { error: run.error })}</p> : null}
          {actionError ? (
            <Banner tone="error" role="alert">
              {actionError}
            </Banner>
          ) : null}
          {skipped.length > 0 ? (
            <Banner tone="warning" role="status" title={t("onboarding.skippedTitle")} testId="analysis-skipped">
              <ul className="plain-list">
                {skipped.map((s) => (
                  <li key={s.id}>
                    <span className="user-content">{s.label}</span>: {s.reason}
                  </li>
                ))}
              </ul>
            </Banner>
          ) : null}
          {dryRun && wouldClose > 0 ? (
            <Banner tone="info" title={t("onboarding.dryRun", { count: wouldClose })} testId="dry-run">
              {t("onboarding.dryRunBody")}
            </Banner>
          ) : null}

          <section className="stack" aria-labelledby={`review-${run.id}`}>
            <h4 id={`review-${run.id}`} className="subheading">
              {t("onboarding.review")}
            </h4>
            {review.length === 0 ? (
              <p className="subtle">{t("onboarding.reviewEmpty")}</p>
            ) : (
              <ul className="plain-list analysis-list">
                {review.map((item) => (
                  <li key={item.requirement.id} className="analysis-item stack" data-testid="review-item">
                    <div className="row row--wrap">
                      <span className="mono subtle">{item.requirement.external_id}</span>
                      <span className="user-content">{item.requirement.title}</span>
                    </div>
                    <div className="row row--wrap">
                      <StatusPill view={VERDICT_VIEW[item.verdict.verdict]} />
                      <span className="muted">{t(`onboarding.reason.${item.reason}`)}</span>
                      {item.verdict.fixture_only ? <NotHomologated /> : null}
                    </div>
                    <EvidenceRefs refs={item.verdict.evidence.refs} />
                    {item.open_tasks.length > 0 ? (
                      <p className="field-hint">
                        {t("onboarding.openTasks")}:{" "}
                        {item.open_tasks.map((x, i) => (
                          <span key={x.id}>
                            {i > 0 ? ", " : ""}
                            <span className="mono">#{x.number}</span> <span className="user-content">{x.title}</span> ({t(statusView(x.status).label)})
                          </span>
                        ))}
                      </p>
                    ) : null}
                    {answerable ? (
                      <div className="row row--wrap">
                        <Button
                          size="sm"
                          variant="primary"
                          disabled={busy !== null}
                          loading={busy === item.requirement.id}
                          aria-label={t("onboarding.readyFor", { code: item.requirement.external_id })}
                          onClick={() => void answer(item, "ready")}
                        >
                          {t("onboarding.ready")}
                        </Button>
                        <Button size="sm" disabled={busy !== null} aria-label={t("onboarding.notReadyFor", { code: item.requirement.external_id })} onClick={() => void answer(item, "not_ready")}>
                          {t("onboarding.notReady")}
                        </Button>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="stack" aria-labelledby={`closed-${run.id}`}>
            <div className="row row--wrap row--between">
              <h4 id={`closed-${run.id}`} className="subheading">
                {t("onboarding.closed")}
              </h4>
              {answerable && reopenable ? (
                <Button size="sm" variant="danger" disabled={busy !== null} onClick={() => setUndo({ kind: "all", ids: closed.filter((c) => c.reopenable).map((c) => c.task.id) })}>
                  {t("onboarding.reopenAll")}
                </Button>
              ) : null}
            </div>
            {closed.length === 0 ? (
              <p className="subtle">{t("onboarding.closedEmpty")}</p>
            ) : (
              <ul className="plain-list analysis-list">
                {closed.map((c) => (
                  <li key={c.task.id} className="analysis-item stack" data-testid="closed-item">
                    <div className="row row--wrap">
                      <span className="mono">#{c.task.number}</span>
                      <span className="user-content">{c.task.title}</span>
                      <span className="mono subtle">{c.requirement_external_id}</span>
                      <StatusPill view={statusView(c.task.status)} />
                      {c.fixture_only ? <NotHomologated /> : null}
                    </div>
                    <EvidenceRefs refs={c.refs} />
                    {answerable && c.reopenable ? (
                      <div className="row">
                        <Button size="sm" disabled={busy !== null} aria-label={t("onboarding.reopenFor", { number: c.task.number })} onClick={() => setUndo({ kind: "one", item: c })}>
                          {t("onboarding.reopen")}
                        </Button>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </section>
          <ConfirmDialog
            open={undo !== null}
            title={undo?.kind === "one" ? t("onboarding.reopenFor", { number: undo.item.task.number }) : t("onboarding.reopenAll")}
            body={
              undo ? (
                <div className="stack">
                  <p>{undo.kind === "one" ? t("onboarding.reopenExistingBody") : t("onboarding.reopenAllBody")}</p>
                  <BoardStartedDependents projectId={project.id} ids={undo.kind === "one" ? [undo.item.task.id] : undo.ids} />
                </div>
              ) : null
            }
            confirmLabel={undo?.kind === "one" ? t("onboarding.reopen") : t("onboarding.reopenAll")}
            danger
            onClose={() => setUndo(null)}
            onConfirm={() => (undo ? confirmUndo(undo) : Promise.resolve())}
          />
        </>
      ) : null}
    </section>
  );
}
